import React, { useEffect } from 'react';
import { Route, Routes, Navigate, Link as RouterLink, useLocation } from 'react-router-dom';
import { useRefCapture } from './hooks/useRefCapture';
import { Container, Link, CircularProgress, Box } from '@mui/material';
import { AuthProvider, useAuth } from './auth';
import Layout from './components/Layout';
import RequirePermission from './components/RequirePermission';
import { ORGANIZATION_EDIT_PERMISSION, ORGANIZATION_LIST_USERS_PERMISSION } from './constants/permissions';
import OrganisationSettings from './pages/OrganisationSettings';
import OrganizationSetupInfo from './pages/OrganizationSetupInfo';
import Dashboard from './pages/Dashboard';
import Products from './pages/Products';
import NewProduct from './pages/NewProduct';
import ProductDetails from './pages/ProductDetails';
import TranslationEdit from './pages/TranslationEdit';
import NewTranslation from './pages/NewTranslation';
import Printing from './pages/Printing';
import OrganizationUsers from './pages/OrganizationUsers';
import NotFound from './pages/NotFound';
import UserSettings from './pages/UserSettings';
import BillingSubscribe from './pages/BillingSubscribe';
import BillingInfo from './pages/BillingInfo';
import BillingStore from './pages/BillingStore';
import Welcome from './pages/Welcome';
import PublicClpEmbed from './pages/PublicClpEmbed';

const Callback: React.FC = () => {
  const { authenticated, isLoading, handleCallback } = useAuth();
  
  useEffect(() => {
    handleCallback();
  }, [handleCallback]);

  if (isLoading) {
    return (
      <Container>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
          <div style={{ marginLeft: '16px' }}>Processing authentication...</div>
        </Box>
      </Container>
    );
  }

  if (authenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <Container>
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        Authentication failed. <Link component={RouterLink} to="/">Try again</Link>
      </Box>
    </Container>
  );
};

const RequireAuth: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { authenticated, isLoading, login } = useAuth();
  
  if (isLoading) {
    return (
      <Container>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }
  
  if (!authenticated) {
    return <Welcome onLogin={login} />;
  }
  
  return <>{children}</>;
};

const App: React.FC = () => {
  const location = useLocation();
  useRefCapture(location);
  const isPublicEmbedRoute = location.pathname.startsWith('/embed/clp/');

  if (isPublicEmbedRoute) {
    return (
      <Routes>
        <Route path="/embed/clp/:publicKey" element={<PublicClpEmbed />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    );
  }

  return (
    <AuthProvider>
      <Layout>
        <Routes>
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/callback" element={<Callback />} />
          <Route path="/dashboard" element={<RequireAuth><Dashboard /></RequireAuth>} />
          <Route path="/settings" element={<RequireAuth><UserSettings /></RequireAuth>} />
          <Route
            path="/organization/setup"
            element={
              <RequireAuth>
                <RequirePermission permission={ORGANIZATION_EDIT_PERMISSION} fallbackPath="/organization/setup-info">
                  <OrganisationSettings />
                </RequirePermission>
              </RequireAuth>
            }
          />
          <Route
            path="/organization/setup-info"
            element={
              <RequireAuth>
                <OrganizationSetupInfo />
              </RequireAuth>
            }
          />
          <Route path="/products" element={<RequireAuth><Products /></RequireAuth>} />
          <Route path="/products/new" element={<RequireAuth><NewProduct /></RequireAuth>} />
          <Route path="/products/:id" element={<RequireAuth><ProductDetails /></RequireAuth>} />
          <Route path="/products/:id/translations/new" element={<RequireAuth><NewTranslation /></RequireAuth>} />
          <Route path="/products/:id/translations/:language/edit" element={<RequireAuth><TranslationEdit /></RequireAuth>} />
          <Route path="/printing" element={<RequireAuth><Printing /></RequireAuth>} />
          <Route
            path="/billing/subscribe"
            element={
              <RequireAuth>
                <RequirePermission permission={ORGANIZATION_EDIT_PERMISSION} fallbackPath="/billing/info">
                  <BillingSubscribe />
                </RequirePermission>
              </RequireAuth>
            }
          />
          <Route
            path="/billing/info"
            element={
              <RequireAuth>
                <BillingInfo />
              </RequireAuth>
            }
          />
          <Route
            path="/billing/store"
            element={
              <RequireAuth>
                <BillingStore />
              </RequireAuth>
            }
          />
          <Route
            path="/users"
            element={
              <RequireAuth>
                <RequirePermission permission={ORGANIZATION_LIST_USERS_PERMISSION}>
                  <OrganizationUsers />
                </RequirePermission>
              </RequireAuth>
            }
          />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Layout>
    </AuthProvider>
  );
};

export default App;
